import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classes from './InputBox.module.css'
import { formatSSN, phoneFormat } from '../../../lib/script';
import { useUserTabEditPermissionDisabled, useCanEditArchived } from '../../../lib/usersPermissions';
import RemoveDataConfirmationModal from '../RemoveDataConfirmationModal';



const InputBox = ({label, type, name, required, placeholder, icon, passView, currentValue, action, identifier, maxWidth, minWidth, format, charLimit, maxNumericValue, isNotModifiable, setStyle, redBorderFlag, yellowBorderFlag, greenBorderFlag, hideSSN, ssnLastFour, isAFilter, removeModalPosition, bkTransparent, suggestions, onSuggestionSelect, apiDisabled, showApiLoader, allowNegative = false, title}) => {
    // let masterDisable = useCanEdit()
    let editArchivedDisabled = useCanEditArchived()
    let userPermissionDisabled = useUserTabEditPermissionDisabled(identifier?.permissionType ? identifier?.permissionType : identifier?.type)

    const inputRef = useRef(null);
    const skipBlurCommitRef = useRef(false)
    const didCutShortcutRef = useRef(false)
    const lastCommittedZipRef = useRef('')
    const [isfocused, setIsFocused] = useState(false)
    const [showPassword, setShowPassword] = useState(false);
 
    const [defaultValue, setDefaultValue] = useState(currentValue ? currentValue : '')
    const [currencyRawValue, setCurrencyRawValue] = useState('')
    const [showSuggestions, setShowSuggestions] = useState(false)

    const [openConfirmationModal, setOpenConfirmationModal] = useState(false)

    const currencyFormatter = useMemo(() => {
        if (format !== 'currency') return null
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            trailingZeroDisplay: 'stripIfInteger',
        })
    }, [format])

    const sanitizeCurrencyRaw = useCallback((input) => {
        if (input == null) return ''
        const raw = `${input}`.trim()
        if (!raw) return ''

        // Detect leading minus for Work Q currency ranges (and similar). Policy-limit fields keep allowNegative false.
        const negative = allowNegative && (
            /^\(.*\)$/.test(raw) ||
            /^-/.test(raw.replace(/[\s,$]/g, '')) ||
            /^\$\s*-/.test(raw)
        )

        // Keep only digits and dot, strip currency symbols/grouping/spaces.
        const s = raw.replace(/[^0-9.]/g, '')
        // Allow typing a lone "-" while focused when negatives are enabled.
        if (!s) {
            return allowNegative && (raw === '-' || raw.startsWith('-')) ? '-' : ''
        }
        const firstDot = s.indexOf('.')
        let magnitude
        if (firstDot === -1) {
            magnitude = s
        } else {
            const intPart = s.slice(0, firstDot)
            const decPart = s
                .slice(firstDot + 1)
                .replace(/\./g, '') // remove any extra dots
                .slice(0, 2) // max 2 decimals
            magnitude = `${intPart}.${decPart}`
        }
        return negative ? `-${magnitude}` : magnitude
    }, [allowNegative])

    const formatCurrencyDisplay = useCallback((raw) => {
        const clean = sanitizeCurrencyRaw(raw)
        if (!clean || clean === '-') return ''
        const asNumber = Number(clean)
        if (Number.isNaN(asNumber) || !Number.isFinite(asNumber)) return ''
        const absFormatted = currencyFormatter
            ? currencyFormatter.format(Math.abs(asNumber))
            : `${Math.abs(asNumber)}`
        return asNumber < 0 ? `-$${absFormatted}` : `$${absFormatted}`
    }, [currencyFormatter, sanitizeCurrencyRaw])

    const foucsHandler = () => {
        if (!userPermissionDisabled) { // check for user permission first
            setIsFocused(true)
            if (format === 'currency') {
                setDefaultValue(currencyRawValue)
            }
            if (suggestions && suggestions.length > 0) {
                setShowSuggestions(true)
            }

            if (hideSSN) {
                setDefaultValue('')
            }
        }
        
    }

    const normalizeComparableValue = (value) => (
        value == null || `${value}` === '' ? '' : `${value}`
    )

    const commitValue = (value, { forceZipCommit = false } = {}) => {
        if (!action) return false

        const normalizedValue = normalizeComparableValue(value)
        const shouldCommit = format === 'zip' && forceZipCommit
            ? normalizedValue.length === 5 && normalizedValue !== lastCommittedZipRef.current
            : normalizedValue !== normalizeComparableValue(currentValue)

        if (shouldCommit) {
            action({
                data: {
                    value
                },
                identifier: identifier,
                option: {
                    label: value,
                    value
                }
            })
            if (format === 'zip' && normalizedValue.length === 5) {
                lastCommittedZipRef.current = normalizedValue
            }
            return true
        }

        return false
    }

    const blurHandler = (e) => {
        if (!userPermissionDisabled) { // check for user permission first
            if (skipBlurCommitRef.current) {
                skipBlurCommitRef.current = false
                setShowSuggestions(false)
                return
            }

            const nextValue = ssnLastFour
                ? e.target.value.replace(/[^0-9]/g, "").slice(0, 4)
                : e.target.value
            const hadSavedValue = currentValue != null && `${currentValue}` !== ''
            const didCutToEmpty = didCutShortcutRef.current && `${nextValue}` === ''
            didCutShortcutRef.current = false

            // If Ctrl/Cmd+X resulted in an empty value, treat it as a clear action that must be confirmed.
            // Restore the previous value immediately so we don't end up showing an unsaved empty field on cancel.
            if (didCutToEmpty && hadSavedValue && !isAFilter) {
                setDefaultValue(`${currentValue}`)
                setIsFocused(true)
                setShowSuggestions(false)
                setOpenConfirmationModal(true)
                return
            }

            if (format === 'currency') {
                const raw = sanitizeCurrencyRaw(currencyRawValue)
                const prevRaw = sanitizeCurrencyRaw(currentValue)

                if (raw === '' || raw === '-') {
                    setIsFocused(false)
                    setDefaultValue('')
                    if (prevRaw !== '' && prevRaw !== '-') {
                        commitValue(null)
                    }
                } else {
                    // Keep the label floated when we have a value to avoid overlap flicker.
                    setIsFocused(true)
                    setDefaultValue(formatCurrencyDisplay(raw))
                    if (raw !== prevRaw) {
                        commitValue(raw)
                    }
                }
                setShowSuggestions(false)
                return
            }

            if (format === 'zip') {
                const numericZip = nextValue.replace(/[^0-9]/g, '').slice(0, 5)
                setDefaultValue(numericZip)
                if (numericZip === '') {
                    setIsFocused(false)
                }
                commitValue(numericZip, { forceZipCommit: true })
                setShowSuggestions(false)
                return
            }

            if (nextValue == '') {
                // setIsFocused(false)

                if (hideSSN) {
                    setDefaultValue(currentValue ? currentValue : '')
                    setShowSuggestions(false)
                    return
                } else {
                    setIsFocused(false)
                }
            }    
            
            commitValue(nextValue)
            setShowSuggestions(false)
        }
        
    }

    function clearSelectionHandler() {
        if (!userPermissionDisabled) { // check for user permission first
            // Treat clear-click as an explicit user action and avoid blur-driven commits.
            skipBlurCommitRef.current = true

            const hasSavedValue = currentValue != null && `${currentValue}` !== ''
            const hasLocalValue = format === 'currency'
                ? sanitizeCurrencyRaw(currencyRawValue || defaultValue) !== ''
                : `${defaultValue ?? ''}` !== ''

            if (!(hasSavedValue || hasLocalValue)) {
                return
            }

            if (!isAFilter) {
                setOpenConfirmationModal(true)
            } else {
                removeSelection()
            }
        }
        
    }

    function closeConfirmationModal() {
        setOpenConfirmationModal(false)
    }

    function removeSelection() {
        if (!userPermissionDisabled) { // check for user permission first
            inputRef.current.value = "";
            setDefaultValue('')
            if (format === 'currency') {
                setCurrencyRawValue('')
            }
            setIsFocused(false)
            setShowSuggestions(false)

            if (action) {
                action({
                    data: {
                        value: null
                    },
                    identifier: identifier
                })
            }
            setOpenConfirmationModal(false)
        }
        
    }

    const viewPasswordHandler = () => {
        setShowPassword(prevState => prevState = !prevState)
    }

    function selectSuggestion(option) {
        skipBlurCommitRef.current = true
        const value = option?.value != null ? `${option.value}` : ''
        if (format === 'currency') {
            const raw = sanitizeCurrencyRaw(value)
            setCurrencyRawValue(raw)
            setDefaultValue(raw)
        } else {
            setDefaultValue(value)
        }
        setShowSuggestions(false)
        if (onSuggestionSelect) {
            onSuggestionSelect(option)
        } else {
            if (format === 'currency') {
                commitValue(value ? sanitizeCurrencyRaw(value) : null)
            } else {
                commitValue(value)
            }
        }
    }

    useEffect(() => {
        if (format === 'currency') {
            const raw = sanitizeCurrencyRaw(currentValue)
            setCurrencyRawValue(raw)
            setDefaultValue(raw ? formatCurrencyDisplay(raw) : '')
            if (raw !== '') {
                setIsFocused(true)
            }
            return
        }

        const nextValue = currentValue ? `${currentValue}` : ''
        setDefaultValue(nextValue)
        if (format === 'zip') {
            lastCommittedZipRef.current = nextValue.replace(/[^0-9]/g, '').slice(0, 5)
        }
        if (currentValue) {
            setIsFocused(true)
        }
    }, [currentValue, format, sanitizeCurrencyRaw, formatCurrencyDisplay])


    const handleKeyDown = (event) => {
        if (!userPermissionDisabled) { // check for user permission first
            if ((event.ctrlKey || event.metaKey) && `${event.key}`.toLowerCase() === 'x') {
                didCutShortcutRef.current = true
            }
            if (event.key == 'Enter') {
                if (format === 'currency') {
                    const raw = sanitizeCurrencyRaw(currencyRawValue)
                    const prevRaw = sanitizeCurrencyRaw(currentValue)
                    const isEmpty = raw === '' || raw === '-'
                    setCurrencyRawValue(isEmpty ? '' : raw)
                    setDefaultValue(isEmpty ? '' : raw)
                    setIsFocused(!isEmpty)
                    const nextValue = isEmpty ? null : raw
                    if ((isEmpty && prevRaw !== '' && prevRaw !== '-') || (!isEmpty && raw !== prevRaw)) {
                        commitValue(nextValue)
                    }
                } else if (ssnLastFour) {
                    const nextValue = event.target.value.replace(/[^0-9]/g, "").slice(0, 4)
                    setDefaultValue(nextValue)
                    commitValue(nextValue)
                } else {
                    setDefaultValue(event.target.value)
                    commitValue(event.target.value)
                }
                setShowSuggestions(false)
            }
        }
        
    }

    const keyuphandler = (event) => {
        if (!userPermissionDisabled) { // check for user permission first
            if (format && format == 'phone') {
                const numericValue = event.target.value.replace(/[^0-9]/g, "");

                if (numericValue) {
                    setDefaultValue(phoneFormat(numericValue))  
                }
            } else if (format && format == '100max') {
                const numericValue = event.target.value.replace(/[^0-9]/g, "");
                if (maxNumericValue) {
                    if (numericValue > maxNumericValue) {
                        setDefaultValue(100)
                    } else {
                        setDefaultValue(numericValue.slice(0,3))
                    }
                } else {
                    setDefaultValue(numericValue.slice(0,3))
                }
                
            } else if (format && format == 'number') {
                const numericValue = event.target.value.replace(/[^0-9]/g, "");
                if (charLimit) {
                    setDefaultValue(numericValue.slice(0,charLimit))
                } else if (maxNumericValue) {
                    if (numericValue > maxNumericValue) {
                        setDefaultValue(maxNumericValue)
                    } else {
                        setDefaultValue(numericValue)
                    }
                }else {
                    setDefaultValue(numericValue)
                }
            } else if (format && format == 'currency') {
                const raw = sanitizeCurrencyRaw(event.target.value)
                setCurrencyRawValue(raw)
                setDefaultValue(raw)
            } else if (format && format == 'zip') {            
                const numericValue = event.target.value.replace(/[^0-9]/g, "");
                setDefaultValue(numericValue.slice(0,5))
            } else if (ssnLastFour) {
                const numericValue = event.target.value.replace(/[^0-9]/g, "");
                setDefaultValue(numericValue.slice(0,4))
            } else if (format && format == 'ssn') {
                const numericValue = event.target.value.replace(/[^0-9]/g, "");
                setDefaultValue(formatSSN(numericValue))
            } else {
                setDefaultValue(event.target.value)
            }
            if (suggestions && suggestions.length > 0) {
                setShowSuggestions(true)
            }
        }        
    }

    const availableSuggestions = (suggestions || []).map((suggestion) => {
        if (suggestion && typeof suggestion === 'object') {
            return {
                ...suggestion,
                value: suggestion.value != null ? `${suggestion.value}` : '',
                searchLabel: `${suggestion.searchLabel ?? suggestion.label ?? suggestion.value ?? ''}`,
                label: `${suggestion.label ?? suggestion.value ?? ''}`
            }
        }

        return {
            value: `${suggestion}`,
            label: `${suggestion}`,
            searchLabel: `${suggestion}`
        }
    }).filter((suggestion, index, array) => (
        array.findIndex((item) => item.value === suggestion.value && item.label === suggestion.label) === index
    ))
    const normalizedInput = `${(format === 'currency' ? currencyRawValue : defaultValue) || ''}`.toLowerCase().trim()
    const filteredSuggestions = availableSuggestions.filter((item) => {
        if (!normalizedInput) return true
        return item.searchLabel.toLowerCase().includes(normalizedInput) || item.value.toLowerCase().includes(normalizedInput)
    }).slice(0, 8)

    const isDisabled = Boolean(apiDisabled || isNotModifiable || editArchivedDisabled || userPermissionDisabled)

    return <div className={`${classes.input_box} ${setStyle ? classes[setStyle] : ''} ${greenBorderFlag ? classes.has_green_border : ''} ${!greenBorderFlag && redBorderFlag ? classes.has_red_border : ''} ${yellowBorderFlag ? classes.has_yellow_border : ''} ${bkTransparent ? classes.bk_transparent : ''}`} style={{maxWidth: maxWidth, minWidth: minWidth}} title={title || undefined}>
        {icon && <div className={classes.input_box_icon}><i className={icon}></i></div>}
        <div className={`${classes.input_box_fields} ${(isfocused && label) ? classes.focused : ''} `}>
            {label && <label htmlFor={name} className={classes.label}>{label}{required ? <span className={'required'}>*</span> : ''}{showApiLoader && apiDisabled ? <span className="loader dark field_label_loader" /> : null}</label>}
            <input autoComplete="off" ref={inputRef} className={classes.input}  type={!showPassword ? type : 'text'} name={name} id={name} required={required} placeholder={placeholder} onFocus={foucsHandler} onBlur={blurHandler} onChange={keyuphandler} onKeyDown={handleKeyDown} value={defaultValue} disabled={isDisabled} title={title || undefined}/>
            {showSuggestions && filteredSuggestions.length > 0 && (
                <div className={classes.suggestions_dropdown}>
                    {filteredSuggestions.map((suggestion) => (
                        <button
                            key={`${identifier?.field || name}-sugg-${suggestion.value}-${suggestion.label}`}
                            type='button'
                            className={classes.suggestion_item}
                            onMouseDown={(event) => {
                                event.preventDefault()
                                selectSuggestion(suggestion)
                            }}
                        >
                            <span className={classes.suggestion_primary}>{suggestion.label}</span>
                        </button>
                    ))}
                </div>
            )}
            {isfocused && !isNotModifiable && <button
                className={classes.clear_input}
                type='button'
                onMouseDown={(event) => {
                    // Ensure blur handler doesn't commit an old value before clear click runs.
                    event.preventDefault()
                    skipBlurCommitRef.current = true
                }}
                onClick={clearSelectionHandler}
                disabled={isDisabled}
            >
                <i className='ti ti-x'></i>
                <span className='sr-only'>clear input</span>
            </button>}
            
            {passView && <button onClick={viewPasswordHandler} className={classes.show_pass} type='button'>
                <i className='ti ti-eye'></i>
                <span className='sr-only'>show password</span>
            </button>}
        </div>
        {openConfirmationModal && <RemoveDataConfirmationModal closeModal={closeConfirmationModal} removeData={removeSelection} position={removeModalPosition} />}
    </div>
}

export default InputBox